import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Building2, Send, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interests = [
  { id: 'innovation', label: 'Innovation technologique' },
  { id: 'bim', label: 'BIM & Modélisation 3D' },
  { id: 'durable', label: 'Construction durable' },
  { id: 'drones', label: 'Drones & IoT' },
  { id: 'networking', label: 'Networking & B2B' },
  { id: 'financement', label: 'Financement' },
];

const ForumRegistrationModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    full_name: '', email: '', company: '', job_title: '', phone: '', interests: [], message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [badgeUrl, setBadgeUrl] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleInterestToggle = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(i => i !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/forum/register`, formData);
      setSuccess(true);
      setBadgeUrl(res.data.badge_url || '');
      toast.success('Inscription réussie');
    } catch (error) {
      toast.error("Erreur lors de l'inscription");
    } finally { setLoading(false); }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSuccess(false);
      setBadgeUrl('');
      setFormData({ full_name: '', email: '', company: '', job_title: '', phone: '', interests: [], message: '' });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto p-0 bg-white border-0 shadow-2xl rounded-xl" data-testid="forum-registration-modal">
        {success ? (
          <div className="py-12 text-center px-8">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">Inscription confirmée</h3>
            <p className="text-[#6B7C93] text-sm mb-6">
              Merci pour votre intérêt ! Votre badge est prêt.
            </p>
            {badgeUrl && (
              <a
                href={`${BACKEND_URL}${badgeUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="form-submit-btn inline-flex w-auto px-8"
                data-testid="download-badge-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger mon badge
              </a>
            )}
            <p className="text-[#94A3B8] text-xs mt-4">
              Un lien de téléchargement sera également envoyé à votre adresse email.
            </p>
            <button onClick={handleClose} className="mt-4 text-sm text-[#6B7C93] hover:text-[#1E3A5F] transition-colors" data-testid="close-success-btn">
              Fermer
            </button>
          </div>
        ) : (
          <>
            <div className="form-card-header rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F5A623]/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#F5A623]" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-[#1E3A5F]">Exposer vos solutions</DialogTitle>
                  <DialogDescription className="text-[#6B7C93] text-sm mt-0.5">Inscrivez-vous pour participer au Forum URBANIK 2026</DialogDescription>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label className="form-label">Nom complet <span className="text-[#F5A623]">*</span></Label>
                  <Input name="full_name" value={formData.full_name} onChange={handleChange} required className="form-input" placeholder="Jean Dupont" data-testid="forum-fullname-input" />
                </div>
                <div className="form-group">
                  <Label className="form-label">Email <span className="text-[#F5A623]">*</span></Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="form-input" placeholder="jean@example.com" data-testid="forum-email-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label className="form-label">Entreprise <span className="text-[#F5A623]">*</span></Label>
                  <Input name="company" value={formData.company} onChange={handleChange} required className="form-input" placeholder="Nom de la société" data-testid="forum-company-input" />
                </div>
                <div className="form-group">
                  <Label className="form-label">Fonction <span className="text-[#F5A623]">*</span></Label>
                  <Input name="job_title" value={formData.job_title} onChange={handleChange} required className="form-input" placeholder="Directeur technique" data-testid="forum-jobtitle-input" />
                </div>
              </div>

              <div className="form-group">
                <Label className="form-label">Téléphone <span className="text-[#F5A623]">*</span></Label>
                <Input name="phone" value={formData.phone} onChange={handleChange} required className="form-input" placeholder="+225 00 00 00 00" data-testid="forum-phone-input" />
              </div>

              <div className="form-group">
                <Label className="form-label mb-3 block">Centres d'intérêt</Label>
                <div className="grid grid-cols-2 gap-3">
                  {interests.map((interest) => (
                    <label key={interest.id} className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${formData.interests.includes(interest.id) ? 'border-[#1E3A5F] bg-[#1E3A5F]/5' : 'border-[#E1E8F0] hover:border-[#94A3B8]'}`}>
                      <Checkbox
                        id={interest.id}
                        checked={formData.interests.includes(interest.id)}
                        onCheckedChange={() => handleInterestToggle(interest.id)}
                        className="border-[#C4CDD5] data-[state=checked]:bg-[#1E3A5F] data-[state=checked]:border-[#1E3A5F]"
                        data-testid={`forum-interest-${interest.id}`}
                      />
                      <span className="text-sm text-[#4A5568]">{interest.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <Label className="form-label">Message (optionnel)</Label>
                <Textarea name="message" value={formData.message} onChange={handleChange} className="form-input min-h-[80px] resize-none" placeholder="Vos attentes pour le forum..." data-testid="forum-message-input" />
              </div>

              <button type="submit" disabled={loading} className="form-submit-btn w-full" data-testid="forum-submit-btn">
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Inscription en cours...</>) : (<><Send className="w-4 h-4 mr-2" />Confirmer mon inscription</>)}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForumRegistrationModal;
